import { Link } from 'react-router-dom'
import { Esqueleto } from '../../components/Estados'
import { IconeHistorico, IconeSeta } from '../../components/Icones'
import { TERMOS } from '../../lib/glossario'
import { contagemPorExtenso, type Contagem } from './contagens'
import { enderecoDaLista, HISTORICO_IMPORTADO } from './recortes'

const ID_DO_TITULO = 'painel-historico-do-sistema-anterior'

/**
 * Quantas vieram do sistema anterior, por extenso.
 *
 * @param props a contagem do recorte de histórico
 * @returns a frase com o número, ou o espaço reservado enquanto ele não chega
 */
function Quantas({ contagem }: { contagem: Contagem }) {
  if (contagem.carregando) {
    return <Esqueleto linhas={1} className="mt-2 w-40" />
  }

  if (contagem.quantidade === null) {
    return <p className="mt-2 text-sm text-slate-600">Não foi possível contar agora.</p>
  }

  return (
    <p className="mt-2 text-2xl font-bold tabular-nums text-slate-900">
      {contagemPorExtenso(contagem.quantidade)}{' '}
      <span className="text-sm font-normal text-slate-700">
        {contagem.quantidade === 1 ? 'solicitação importada' : 'solicitações importadas'}
      </span>
    </p>
  )
}

/**
 * O histórico do sistema anterior, num bloco à parte de propósito.
 *
 * Ele é o único número desta tela que NÃO é do processo: está fora da grade, com outra cor, outra
 * moldura e um texto que diz o que ele é. Misturá-lo aos cartões — ou, pior, somá-lo a eles — faria
 * o painel afirmar 1.025 aprovações de comitê que nunca aconteceram, que é exatamente o que o
 * `CLAUDE.md` manda não fazer com esses registros.
 *
 * O número é contado ao vivo (`origem=MIGRADO_V1`) e o texto ao lado é o do glossário, que hoje
 * cita 1.027. Se um dia a importação trouxer mais, os dois se contradizem à vista — e o conserto é
 * uma string em `glossario.ts`, não um número escondido aqui.
 *
 * @param props a contagem do recorte de histórico
 * @returns o cartão do histórico
 */
export function CartaoDoHistorico({ contagem }: { contagem: Contagem }) {
  return (
    <section
      aria-labelledby={ID_DO_TITULO}
      className="flex gap-4 rounded-xl border border-slate-300 border-l-4 border-l-slate-500 bg-slate-100 p-5"
    >
      <IconeHistorico className="mt-1 h-6 w-6 shrink-0 text-slate-500" />
      <div className="min-w-0">
        <h2 id={ID_DO_TITULO} className="text-base font-semibold text-slate-900">
          {TERMOS.historicoMigrado.rotulo}
        </h2>
        <Quantas contagem={contagem} />
        <p className="mt-2 max-w-prose text-sm text-slate-700">{TERMOS.historicoMigrado.ajuda}</p>
        <Link
          to={enderecoDaLista(HISTORICO_IMPORTADO)}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-niteroi-700 hover:text-niteroi-900"
        >
          Consultar o histórico
          <IconeSeta className="h-4 w-4" />
        </Link>
      </div>
    </section>
  )
}
